import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {
  AllocatableLoadSettingsEntity,
  DVIREntity,
  useDeleteAllocatableLoadSettingsMutation
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {ActionIcon, Group, Text} from "@mantine/core";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {useNavigate} from "react-router-dom";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {modals} from "@mantine/modals";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {FormattedQuantity, getFormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity"
import {Badge} from "../../badge/Badge/Badge";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";

const EMPTY_ARRAY: AllocatableLoadSettingsEntity[] = []

export type AllocatableLoadSettingsDataTableProps = EntityDataTableProps<AllocatableLoadSettingsEntity>

export const AllocatableLoadSettingsDataTable = (props: AllocatableLoadSettingsDataTableProps) => {
  const {
    records: settingRecords = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: settingRecords,
    sortStatus: {columnAccessor: 'effectiveDttm', direction: 'desc'},
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const [deleteAllocatableLoadSettings, {isLoading: isDeleting}] = useDeleteAllocatableLoadSettingsMutation();

  useEffect(() => {
    if (isDeleting) {
      notifications.show({
        id: "allocatableLoadSettingsDataTable_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.allocatableloadsettings", "Allocatable Load Settings"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("allocatableLoadSettingsDataTable_deleting")
    }
  }, [isDeleting, t]);

  const onEditClick = useCallback((settings: AllocatableLoadSettingsEntity) => {
    navigate("/allocatable-load-settings/form", {
      state: {
        id: settings.id,
      }
    });
  }, [navigate])

  const onDelete = useCallback((settings: AllocatableLoadSettingsEntity) => {
    deleteAllocatableLoadSettings(settings.id)
      .unwrap()
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.allocatableloadsettings", "Allocatable Load Settings") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
        })
      );
  }, [deleteAllocatableLoadSettings, t])

  const onDeleteClick = useCallback((settings: AllocatableLoadSettingsEntity) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.allocatableloadsettings", "Allocatable Load Settings"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.allocatableloadsettings.confirm", "Are you sure you want to delete these allocatable load settings?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        onDelete(settings)
      },
    })
  }, [onDelete, t]);

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          title: '',
          accessor: "active",
          width: 35,
          render: (settings) => {
            if (settings.isActive()) {
              return <Badge color="green">{t("common.status.active", "Active")}</Badge>
            }

            return undefined
          }
        },
        {
          title: t("common.entity.allocatableloadsettings.table.column.effectivedttm", "Effective Date"),
          accessor: "effectiveDttm",
          width: 100,
          sortable: true,
          render: ({effectiveDttm}) => (effectiveDttm ? <FormattedDateTime date={effectiveDttm}/> : undefined)
        },
        {
          title: t("common.entity.allocatableloadsettings.table.column.category", "Category"),
          accessor: "category",
          width: 75,
          sortable: true,
          render: ({category}) => (
            <>
              {t(category.displayValue.id, category.displayValue.defaultMessage)}
            </>
          ),
        },
        {
          title: t("common.entity.allocatableloadsettings.table.column.loadvolume", "Load Volume"),
          accessor: "loadVolume",
          width: 100,
          sortable: true,
          render: ({loadVolume, volumeUnitOfMeasure}) => (
            <FormattedQuantity
              value={{
                number: loadVolume,
                units: volumeUnitOfMeasure
              }}/>
          )
        },
        {
          title: t("common.entity.allocatableloadsettings.table.column.loadvolumepct", "Fallback Trigger Fill %"),
          accessor: "laodVolumePct",
          width: 100,
          sortable: true,
          render: ({loadVolumePct}) =>
            loadVolumePct ? `${getFormattedQuantity(loadVolumePct)}%` : undefined
        },
        {
          title: t("common.entity.allocatableloadsettings.table.column.method", "Method"),
          accessor: "method",
          width: 100,
          sortable: true,
          render: ({method}) => (
            <>
              {t(method.displayValue.id, method.displayValue.defaultMessage)}
            </>
          ),
        },
        {
          title: t("common.entity.allocatableloadsettings.table.column.name", "Name"),
          accessor: "displayableName",
          width: 100,
          sortable: true,
        },
        {
          title: '',
          accessor: "actions",
          width: 100,
          textAlignment: 'right',
          render: (settings) => (
            <HasAccess permissions={["edit"]}>
              <Group spacing={4} position="right" noWrap>
                <ActionIcon onClick={() => onEditClick(settings)}>
                  <IconEdit size={16}/>
                </ActionIcon>
                <ActionIcon color="red" onClick={() => onDeleteClick(settings)}>
                  <IconTrash size={16}/>
                </ActionIcon>
              </Group>
            </HasAccess>
          )
        }
      ]}
    />
  );
};
